import { createContext, ReactNode, useCallback, useContext, useEffect } from 'react';

import { Modal, ModalProps } from '@bonusly/design-system';

import { useUserContentDismissal } from '../profile/user-content-dismissal';
import { PromoCandidateStatus, usePromoStore } from './promo-store';

const PromoDismissContext = createContext<() => void>(() => {});

/**
 * Returns a dismiss handler any descendant of <PromoModal> can call to close
 * and persist the dismissal — e.g. to fire on a CTA click so the modal isn't
 * shown again after the user converts.
 */
export const usePromoDismiss = () => useContext(PromoDismissContext);

interface PromoModalProps extends Omit<ModalProps, 'isOpen' | 'onClose' | 'children'> {
	/**
	 * Dismissal namespace — grouping for the user_content_dismissals record.
	 * Defaults to `'promo'`; override only if a promo needs its own grouping.
	 */
	namespace?: string;
	/**
	 * Unique dismissal key within the namespace. Must stay stable forever;
	 * changing it will un-dismiss the modal for every user who saw it.
	 * Version suffixes (e.g. `-v1`) let us re-launch with a new record.
	 */
	name: string;
	/**
	 * Whether the current user should see this promo at all (feature flags,
	 * company settings, permission checks). When false, the modal is fully
	 * inert — no dismissal query runs, no coordination with the queue.
	 */
	enabled: boolean;
	/**
	 * External loading signal from the consumer — useful when eligibility
	 * depends on an async backend query the consumer owns. While true, the
	 * candidate stays `pending` (in addition to the internal dismissal
	 * query's loading state), so it holds its queue slot until the consumer
	 * resolves. Omit (or pass `false`) if the consumer has no external
	 * gating beyond `enabled`.
	 */
	isLoading?: boolean;
	/**
	 * Modal contents. Author provides their own ModalOverlay + ModalContent
	 * so each promo can have its own layout, media, and CTAs.
	 */
	children: ReactNode;
}

/**
 * Wraps a Chakra `<Modal>` with dismissal persistence and coordination so
 * that when multiple promos are mounted at once, only one displays per page
 * load. Priority is strict render order: a later-JSX promo cannot win while
 * an earlier-JSX promo is still resolving its dismissal query, even if it
 * finishes first. Dismissing the winner does not reveal the next one; the
 * next-eligible promo appears on the user's next page load.
 *
 * Important: this is an *interrupting* modal. As soon as the user is
 * eligible and this instance wins the queue, the modal opens automatically
 * and blocks the underlying content until the user dismisses it (X, overlay
 * click, "Got it", or a CTA that calls `usePromoDismiss()`). There is no
 * consumer-controlled `isOpen` — open/closed is derived entirely from
 * eligibility + queue + dismissal state. Reach for this when the promo is
 * worth interrupting the user for; for passive, browseable promos prefer
 * an inline surface (e.g. `<DynamicPromo>`) instead.
 */
export const PromoModal = ({ namespace = 'promo', name, enabled, isLoading = false, children, ...modalProps }: PromoModalProps) => {
	const id = `${namespace}:${name}`;
	const { loading, data, updateUserContentDismissal } = useUserContentDismissal({ namespace, name, enabled });
	const dismissed = !!data?.dismissed_at;
	const status: PromoCandidateStatus = !enabled ? 'ineligible' : loading || isLoading ? 'pending' : dismissed ? 'ineligible' : 'eligible';

	const register = usePromoStore((state) => state.register);
	const unregister = usePromoStore((state) => state.unregister);
	const setStatus = usePromoStore((state) => state.setStatus);
	const winnerId = usePromoStore((state) => state.winnerId);

	useEffect(() => {
		register(id);
		return () => unregister(id);
	}, [id, register, unregister]);

	useEffect(() => {
		setStatus(id, status);
	}, [id, status, setStatus]);

	const dismiss = useCallback(() => updateUserContentDismissal(), [updateUserContentDismissal]);

	const isWinner = winnerId === id;
	if (status !== 'eligible' || !isWinner) return null;

	return (
		<Modal isOpen onClose={dismiss} {...modalProps}>
			<PromoDismissContext.Provider value={dismiss}>{children}</PromoDismissContext.Provider>
		</Modal>
	);
};
